import { RestResource } from "./common"
import { SchemaDeSerBuilder, type SchemaDeSer } from "./deser"

export enum FullyPaidSecuritiesLendingRequestStatus {
  CANCELED = "Canceled",
  ERROR = "Error",
  PENDING = "Pending",
  COMPLETE = "Complete",
  REJECTED = "Rejected",
}

export enum FullyPaidSecuritiesLendingRequestDirection {
  OPT_IN = "Opt In",
  OPT_OUT = "Opt Out",
}

/**
 * the ineligibility statuses will show with the following priority:
 * 1. INELIGIBLE_REGION
 * 2. INELIGIBLE_ACCOUNT_TYPE
 * 3. INSUFFICIENT_BALANCE
 * 4. INELIGIBLE_SUITABILITY
 */
export enum FullyPaidSecuritiesLendingEligibilityStatus {
  ELIGIBLE = "ELIGIBLE",
  INELIGIBLE_REGION = "INELIGIBLE_REGION",
  INELIGIBLE_ACCOUNT_TYPE = "INELIGIBLE_ACCOUNT_TYPE",
  INSUFFICIENT_BALANCE = "INSUFFICIENT_BALANCE",
  INELIGIBLE_SUITABILITY = "INELIGIBLE_SUITABILITY",
}

export enum FullyPaidSecuritiesLendingEligibilityReason {
  INSUFFICIENT_INVESTMENT_OBJECTIVE = "INSUFFICIENT_INVESTMENT_OBJECTIVE",
  INSUFFICIENT_INVESTMENT_TIME_HORIZON = "INSUFFICIENT_INVESTMENT_TIME_HORIZON",
  INSUFFICIENT_RISK_TOLERANCE = "INSUFFICIENT_RISK_TOLERANCE",
  INSUFFICIENT_STOCK_TRADING_EXPERIENCE = "INSUFFICIENT_STOCK_TRADING_EXPERIENCE",
  MISSING_LIQUIDITY_NEEDS = "MISSING_LIQUIDITY_NEEDS",
}

export class FullyPaidSecuritiesLendingEligibility extends RestResource {
  status = FullyPaidSecuritiesLendingEligibilityStatus.ELIGIBLE
  reasons: FullyPaidSecuritiesLendingEligibilityReason[] = []

  get isEligible() {
    return FullyPaidSecuritiesLendingEligibilityStatus.ELIGIBLE === this.status
  }

  get isIneligibleSuitability() {
    return (
      FullyPaidSecuritiesLendingEligibilityStatus.INELIGIBLE_SUITABILITY ===
      this.status
    )
  }

  private hasReason(reason: FullyPaidSecuritiesLendingEligibilityReason) {
    return this.reasons.includes(reason)
  }

  get isIneligibleDueToInvestmentObjective() {
    return this.hasReason(
      FullyPaidSecuritiesLendingEligibilityReason.INSUFFICIENT_INVESTMENT_OBJECTIVE
    )
  }

  get isIneligibleDueToInvestmentTimeHorizon() {
    return this.hasReason(
      FullyPaidSecuritiesLendingEligibilityReason.INSUFFICIENT_INVESTMENT_TIME_HORIZON
    )
  }

  get isIneligibleDueToRiskTolerance() {
    return this.hasReason(
      FullyPaidSecuritiesLendingEligibilityReason.INSUFFICIENT_RISK_TOLERANCE
    )
  }

  get isIneligibleDueToStockTradingExperience() {
    return this.hasReason(
      FullyPaidSecuritiesLendingEligibilityReason.INSUFFICIENT_STOCK_TRADING_EXPERIENCE
    )
  }

  get isIneligibleDueToLiquidityNeeds() {
    return this.hasReason(
      FullyPaidSecuritiesLendingEligibilityReason.MISSING_LIQUIDITY_NEEDS
    )
  }
}

export class FullyPaidSecuritiesLendingRequest extends RestResource {
  accountNumber = ""
  createdAt = null
  direction = FullyPaidSecuritiesLendingRequestDirection.OPT_IN
  status = FullyPaidSecuritiesLendingRequestStatus.PENDING
  updatedAt = null

  get hasRequestedOptIn() {
    return FullyPaidSecuritiesLendingRequestDirection.OPT_IN === this.direction
  }

  get isComplete() {
    return FullyPaidSecuritiesLendingRequestStatus.COMPLETE === this.status
  }

  get isFullyOptedIn() {
    return this.hasRequestedOptIn && this.isComplete
  }
}

export const FULLY_PAID_SECURITIES_LENDING_REQUEST_DESER: SchemaDeSer<FullyPaidSecuritiesLendingRequest> =
  new SchemaDeSerBuilder(FullyPaidSecuritiesLendingRequest)
    .ofString("accountNumber")
    .ofDateTime("createdAt")
    .ofString("direction")
    .ofString("id")
    .ofString("status")
    .ofDateTime("updatedAt")
    .toDeSer()

export const FULLY_PAID_SECURITIES_LENDING_ELIGIBILITY_DESER: SchemaDeSer<FullyPaidSecuritiesLendingEligibility> =
  new SchemaDeSerBuilder(FullyPaidSecuritiesLendingEligibility)
    .ofString("status")
    .ofPrimitiveArray("reasons")
    .toDeSer()
